import { Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from '@/layouts/AppLayout';
import { DashboardPage } from '@/modules/dashboard/DashboardPage';
import { InventoryPage } from '@/modules/inventory/InventoryPage';
import { CategoriesPage } from '@/modules/inventory/categories/CategoriesPage';
import { CollectionsPage } from '@/modules/inventory/collections/CollectionsPage';
import { PackagingPage } from '@/modules/inventory/packaging/PackagingPage';
import { FinishedGoodsPage } from '@/modules/finished-goods/FinishedGoodsPage';
import { BarcodesPage } from '@/modules/barcodes/BarcodesPage';
import { CustomersPage } from '@/modules/customers/CustomersPage';
import { VendorsPage } from '@/modules/vendors/VendorsPage';
import { PurchaseOrdersPage } from '@/modules/purchase-orders/PurchaseOrdersPage';
import { PurchaseRequestsPage } from '@/modules/procurement/PurchaseRequestsPage';
import { ProductRecipesPage } from '@/modules/production/ProductRecipesPage';
import { JobWorkOrdersPage } from '@/modules/production/JobWorkOrdersPage';
import { OrdersPage } from '@/modules/orders/OrdersPage';
import { InvoicesPage } from '@/modules/invoices/InvoicesPage';
import { DispatchPage } from '@/modules/dispatch/DispatchPage';
import { DispatchLabelsPage } from '@/modules/dispatch-labels/DispatchLabelsPage';
import { DispatchPartnersPage } from '@/modules/dispatch/DispatchPartnersPage';
import { DispatcherPage } from '@/modules/dispatch/DispatcherPage';
import { DispatchRulesPage } from '@/modules/dispatch/DispatchRulesPage';
import { ReturnsPage } from '@/modules/returns/ReturnsPage';
import { ReturnRequestsPage } from '@/modules/returns/ReturnRequestsPage';
import { RefundsPage } from '@/modules/refunds/RefundsPage';
import { CancelRequestsPage } from '@/modules/cancels/CancelRequestsPage';
import { MyReviewsPage } from '@/modules/cancels/MyReviewsPage';
import { ReportsPage } from '@/modules/reports/ReportsPage';
import { PlatformWarehousesPage } from '@/modules/platform/warehouses/PlatformWarehousesPage';
import { PlatformRedirect } from './PlatformRedirect';
import { GuideHubPage } from '@/modules/guide/GuideHubPage';
import { GuideTrackPage } from '@/modules/guide/GuideTrackPage';
import { ProfilePage } from '@/modules/profile/ProfilePage';
import { TaxSettingsPage } from '@/modules/settings/TaxSettingsPage';
import { ReturnsSettingsPage } from '@/modules/settings/ReturnsSettingsPage';
import { EmailSendersPage } from '@/modules/settings/EmailSendersPage';
import { IntegrationsHealthPage } from '@/modules/settings/IntegrationsHealthPage';
import { TestDataPage } from '@/modules/settings/TestDataPage';
import { RequestReasonsPage } from '@/modules/settings/RequestReasonsPage';
import { PwaLayout } from '@/pwa/PwaLayout';
import { PwaHomePage } from '@/pwa/PwaHomePage';
import { PwaPackPickerPage } from '@/pwa/PwaPackPickerPage';
import { PwaPackScanPage } from '@/pwa/PwaPackScanPage';
import { PwaReturnPickerPage } from '@/pwa/PwaReturnPickerPage';
import { PwaReturnScanPage } from '@/pwa/PwaReturnScanPage';
import { PwaProfilePage } from '@/pwa/PwaProfilePage';
import { MfaSetupPage } from '@/modules/auth/MfaSetupPage';
import { ReconsentPage } from '@/modules/auth/ReconsentPage';
import { SuspendedPage } from '@/modules/suspended/SuspendedPage';
import { NotFoundPage } from '@/modules/not-found/NotFoundPage';
import { SubscriptionGuard } from './SubscriptionGuard';
import { AuthGuardListener } from './AuthRouter';
import { RequireAuth } from './RequireAuth';
import { CanonicalAuthBounce } from './CanonicalAuthBounce';
import { HomeRedirect } from './HomeRedirect';
import { ExitToPlatform } from './ExitToPlatform';
import { ComplianceGate } from './ComplianceGate';

export function AppRouter() {
  return (
    <>
      <SubscriptionGuard />
      <AuthGuardListener />
      <Routes>
        {/* Auth is owned by the platform app (ADR-004) — the ERP renders no
            login / forgot / reset / invite / mfa pages. These routes only
            forward stray hits and bookmarked links (preserving the token
            query) to the canonical sign-in app. */}
        <Route path="login" element={<CanonicalAuthBounce />} />
        <Route path="login/forgot" element={<CanonicalAuthBounce />} />
        <Route path="login/reset" element={<CanonicalAuthBounce />} />
        <Route path="login/invite" element={<CanonicalAuthBounce />} />
        <Route path="login/mfa" element={<CanonicalAuthBounce />} />
        <Route path="accept-invite" element={<CanonicalAuthBounce to="/login/invite" />} />

        {/* PLATFORM_ADMIN bounce — homeForUser sends platform admins here, and
            this leaves the ERP for the platform control-plane app. Layout-less
            and auth-agnostic: it just redirects. */}
        <Route path="exit-to-platform" element={<ExitToPlatform />} />

        {/* Authenticated app. RequireAuth → ComplianceGate (MFA + re-consent) → AppLayout. */}
        <Route element={<RequireAuth />}>
          <Route element={<ComplianceGate />}>
            {/* Warehouse PWA — mobile-first, lives alongside the
                desktop ERP but uses its own minimal layout. Same
                auth + compliance gates as the rest of the app. */}
            <Route element={<PwaLayout />}>
              <Route path="pwa">
                <Route index element={<PwaHomePage />} />
                <Route path="pack" element={<PwaPackPickerPage />} />
                <Route path="pack/:dispatchId" element={<PwaPackScanPage />} />
                <Route path="returns" element={<PwaReturnPickerPage />} />
                <Route path="returns/:returnId" element={<PwaReturnScanPage />} />
                <Route path="profile" element={<PwaProfilePage />} />
              </Route>
            </Route>

            <Route element={<AppLayout />}>
              <Route index element={<HomeRedirect />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="inventory" element={<InventoryPage />} />
              <Route path="inventory/categories" element={<CategoriesPage />} />
              <Route path="inventory/collections" element={<CollectionsPage />} />
              <Route path="inventory/packaging" element={<PackagingPage />} />
              <Route path="finished-goods" element={<FinishedGoodsPage />} />
              <Route path="barcodes" element={<BarcodesPage />} />
              <Route path="customers" element={<CustomersPage />} />
              <Route path="vendors" element={<VendorsPage />} />
              <Route path="raw-materials" element={<InventoryPage kind="RAW_MATERIAL" />} />
              <Route path="purchase-requests" element={<PurchaseRequestsPage />} />
              <Route path="job-work/recipes" element={<ProductRecipesPage />} />
              <Route path="job-work/orders" element={<JobWorkOrdersPage />} />
              <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
              <Route path="orders" element={<OrdersPage />} />
              <Route path="invoices" element={<InvoicesPage />} />
              <Route path="dispatch" element={<DispatchPage />} />
              <Route path="dispatch-labels" element={<DispatchLabelsPage />} />
              <Route path="dispatch/partners" element={<DispatchPartnersPage />} />
              <Route path="dispatch/dispatcher" element={<DispatcherPage />} />
              <Route
                path="dispatch/delivery-tiers"
                element={<Navigate to="/dispatch/dispatcher" replace />}
              />
              <Route path="dispatch/rules" element={<DispatchRulesPage />} />
              {/* Tracking merged into the Dispatcher → Dispatch tab
                  (Moving / Late filter). Old links land there. */}
              <Route
                path="tracking"
                element={<Navigate to="/dispatch/dispatcher" replace />}
              />
              <Route path="cancel-requests" element={<CancelRequestsPage />} />
              <Route path="return-requests" element={<ReturnRequestsPage />} />
              <Route path="returns" element={<ReturnsPage />} />
              <Route path="refunds" element={<RefundsPage />} />
              <Route path="my-reviews" element={<MyReviewsPage />} />
              <Route path="reports" element={<ReportsPage />} />

              <Route path="guide">
                <Route index element={<GuideHubPage />} />
                <Route path=":track" element={<GuideTrackPage />} />
              </Route>

              <Route path="platform">
                {/* Cross-org / platform-admin surfaces moved to the platform
                    control-plane app (ADR-013). PLATFORM_ADMIN is already
                    bounced there on login; these forward stray/bookmarked hits. */}
                <Route index element={<PlatformRedirect to="/platform/admin" />} />
                <Route path="admin" element={<PlatformRedirect to="/platform/admin" />} />
                <Route path="command-center" element={<PlatformRedirect to="/command-center" />} />
                <Route path="tenants" element={<PlatformRedirect to="/tenants" />} />
                <Route path="onboarding" element={<PlatformRedirect to="/tenants/new" />} />
                <Route path="plans" element={<PlatformRedirect to="/subscriptions" />} />
                <Route path="subscriptions" element={<PlatformRedirect to="/subscriptions" />} />
                <Route path="subscriptions/:tenantId" element={<PlatformRedirect to="/subscriptions" />} />
                <Route path="dsar" element={<PlatformRedirect to="/platform/dsar" />} />
                <Route path="guide" element={<PlatformRedirect to="/platform/guide" />} />
                <Route path="settings" element={<PlatformRedirect to="/platform/settings" />} />

                {/* Tenant workspace self-service — single-org, stays in the ERP. */}
                {/* Account/identity self-service moved to platform-app
                    (ERP-DELOAD-003 / ADR-014). */}
                <Route path="organizations" element={<PlatformRedirect to="/platform/organizations" />} />
                <Route path="organizations/:id" element={<PlatformRedirect to="/platform/organizations" />} />
                <Route path="users" element={<PlatformRedirect to="/users" />} />
                <Route path="mobile-app" element={<PlatformRedirect to="/platform/mobile-app" />} />
                {/* Developer/integration cluster moved to platform-app
                    (DEV-TOOLS-HOME / ERP-DELOAD-003 / ADR-014). */}
                <Route path="api-clients" element={<PlatformRedirect to="/platform/api-clients" />} />
                <Route path="api-keys" element={<PlatformRedirect to="/platform/api-keys" />} />
                <Route path="connectors" element={<PlatformRedirect to="/platform/connectors" />} />
                <Route path="api-reference" element={<PlatformRedirect to="/platform/api-reference" />} />
                <Route path="webhooks" element={<PlatformRedirect to="/platform/webhooks" />} />
                <Route path="webhooks/outbound" element={<PlatformRedirect to="/platform/webhooks" />} />
                <Route path="webhooks/inbound" element={<PlatformRedirect to="/platform/webhooks" />} />
                <Route path="api-activity" element={<PlatformRedirect to="/platform/api-activity" />} />
                <Route path="business-errors" element={<PlatformRedirect to="/platform/business-errors" />} />
                <Route path="saved-views" element={<PlatformRedirect to="/platform/saved-views" />} />
                <Route path="api-simulator" element={<PlatformRedirect to="/platform/api-simulator" />} />
                <Route path="portal-preview" element={<PlatformRedirect to="/platform/portal-preview" />} />
                <Route path="warehouses" element={<PlatformWarehousesPage />} />
                <Route path="support-sessions" element={<PlatformRedirect to="/governance/sessions" />} />
                <Route path="audit" element={<PlatformRedirect to="/governance/audit" />} />
                {/* GST/HSN are GLOBAL reference data (no tenantId) owned by the
                    platform — managed in platform-app (ERP-DELOAD-002 / ADR-014). */}
                <Route path="gst-rates" element={<PlatformRedirect to="/platform/gst-rates" />} />
                <Route path="hsn-rates" element={<PlatformRedirect to="/platform/hsn-rates" />} />
                <Route path="gst-sync" element={<PlatformRedirect to="/platform/gst-rates" />} />
              </Route>

              <Route path="settings">
                <Route index element={<Navigate to="/settings/profile" replace />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="tax" element={<TaxSettingsPage />} />
                <Route path="returns" element={<ReturnsSettingsPage />} />
                <Route path="email" element={<EmailSendersPage />} />
                <Route path="request-reasons" element={<RequestReasonsPage />} />
                <Route path="integrations">
                  <Route index element={<Navigate to="/settings/integrations/health" replace />} />
                  <Route path="health" element={<IntegrationsHealthPage />} />
                </Route>
                <Route path="test-data" element={<TestDataPage />} />
              </Route>

              <Route path="suspended" element={<SuspendedPage />} />
              <Route path="mfa-setup" element={<MfaSetupPage />} />
              <Route path="reconsent" element={<ReconsentPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </>
  );
}
